import * as React from "react"
import { Col, Row } from "react-grid-system"
import { useTranslation } from "react-i18next"
import { HorizontalSpacer } from "../atoms/spacers"
import { useAcceptedCountries } from "../../hooks/useAcceptedCountries"
import Select from "./Select"
import TextInput from "./TextInput"
import LoadingSpinner from "../atoms/LoadingSpinner"
import { BillingAddress, ShippingAddress } from "../../utils/models"
import { captureExceptionWithContext } from "../../utils/sentry"

export const DEFAULT_BILLING_ADDRESS_INFO: BillingAddress = {
  address1: "",
  address2: "",
  zipCode: "",
  city: "",
  country: "",
  vat: "",
}

export const DEFAULT_SHIPPING_ADDRESS_INFO: ShippingAddress = {
  address1: "",
  address2: "",
  zipCode: "",
  city: "",
  country: "",
}

export const isShippingAndBillingSameAddress = (
  shipping: ShippingAddress | undefined,
  billing: BillingAddress | undefined,
) =>
  shipping != undefined &&
  billing != undefined &&
  shipping.address1 == billing.address1 &&
  shipping.address2 == billing.address2 &&
  shipping.city == billing.city &&
  shipping.country == billing.country &&
  shipping.zipCode == billing.zipCode

export const updateAddress = (address: ShippingAddress | BillingAddress, update?: ShippingAddress | BillingAddress) => {
  if (!update) {
    return address
  }

  const result = { ...address }
  Object.entries(update).forEach(([key, value]) => {
    if (Object.hasOwn(address, key) && value != undefined) {
      result[key] = value
    }
  })
  return result
}

interface Props {
  address1Value?: string
  address2Value?: string
  zipCodeValue?: string
  cityValue?: string
  countryValue?: string
  vatValue?: string
  address1DefaultValue?: string
  address2DefaultValue?: string
  zipCodeDefaultValue?: string
  cityDefaultValue?: string
  countryDefaultValue?: string
  vatDefaultValue?: string
  disabled?: boolean
  onChange?: (event: React.ChangeEvent) => void
  hideVat?: boolean
  requiredFields?: boolean
}

const AddressForm: React.FunctionComponent<Props> = ({
  address1Value,
  address2Value,
  zipCodeValue,
  cityValue,
  countryValue,
  vatValue,
  address1DefaultValue,
  address2DefaultValue,
  zipCodeDefaultValue,
  cityDefaultValue,
  countryDefaultValue,
  vatDefaultValue,
  disabled,
  onChange,
  hideVat,
  requiredFields,
}) => {
  const { t } = useTranslation()
  const { countries, error, loading } = useAcceptedCountries()

  if (error) {
    captureExceptionWithContext(error, "error fetching accepted countries from backend")
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div>
      <TextInput
        textFieldTitle={t("address")}
        value={address1Value}
        defaultValue={address1DefaultValue}
        onChange={onChange}
        placeholder="Address"
        id="address1"
        name="address1"
        disabled={disabled}
        required={requiredFields}
      />
      <HorizontalSpacer spacing={2.5} />
      <TextInput
        textFieldTitle={t("Extended address (optional)")}
        value={address2Value}
        defaultValue={address2DefaultValue}
        onChange={onChange}
        placeholder="Address line 2"
        disabled={disabled}
        id="address2"
        name="address2"
      />
      <HorizontalSpacer spacing={2.5} />
      <Row>
        <Col md={6}>
          <TextInput
            textFieldTitle="Zip code"
            value={zipCodeValue}
            defaultValue={zipCodeDefaultValue}
            onChange={onChange}
            placeholder="Zip code"
            disabled={disabled}
            id="zipCode"
            name="zipCode"
            required={requiredFields}
          />
        </Col>
        <Col md={6}>
          <TextInput
            textFieldTitle="City"
            value={cityValue}
            defaultValue={cityDefaultValue}
            onChange={onChange}
            placeholder="City"
            disabled={disabled}
            id="city"
            name="city"
            required={requiredFields}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2.5} />

      <Select
        title="Country"
        value={countryValue}
        defaultValue={countryDefaultValue}
        onChange={onChange}
        disabled={disabled}
        options={countries}
        id="country"
        name="country"
        required={requiredFields}
      />

      {!hideVat && (
        <>
          <HorizontalSpacer spacing={2.5} />
          <TextInput
            textFieldTitle="VAT"
            value={vatValue}
            defaultValue={vatDefaultValue}
            onChange={onChange}
            disabled={disabled}
            id="vat"
            name="vat"
          />
        </>
      )}
    </div>
  )
}

export default AddressForm
